'use strict';
namespace Payment {

    type PaymentOption = Core.PaymentOption;
    type SavedCardOption = Core.SavedCardOption;
    type PaymentModel = Core.PaymentModel;
    type PaymentsUtilService = Core.PaymentsUtilService;
    type PaymentCardValidity = {
        invalid?: boolean,
        required?: boolean
    };

    enum PaymentCategory {
        SavedCard = 1,
        PayPal = 2,
        Card = 3
    }
    enum PaymentOptionId {
        PayPal = 101,
    }

    const year = new Date().getFullYear();

    class Payment {

        private cardCanBeSaved: boolean;
        private piPaymentOptions: PaymentOption[];
        private piSavedCards: SavedCardOption[];
        private savedCardsWatch: () => void;
        private viewPage: string;
        private usingOldTemplate: boolean;
        private currentDate: Date = new Date();
        private currentMonth: number = this.currentDate.getMonth();
        private currentYear: number = this.currentDate.getFullYear();
        private onReceive: Function;
        private addCommand: Function;

        public monthFrom: number = 1;
        public months: number[] = _.range(1, 13);
        public years: number[] = _.range(year, year + 21);
        public yearsPast: number [] = _.range(year - 10, year + 1);

        public model: PaymentModel;
        public cardTypeChanged: string;
        public cardPercentage: number;
        public haveSavedCards: boolean;
        public logoList: string[];
        public paymentAmount: number;
        public paymentOptions: PaymentOption[];
        public payPalOption: PaymentOption;

        public savedCards: SavedCardOption[];
        public fullCardDetailsRequired: boolean;
        public isExternalFop: boolean;

        public selectedPaymentOptionId: string; //to keep track of which radio option is selected

        //extra bits for tabbed version
        public paymentCategory: PaymentCategory;
        public selectedSavedCard: SavedCardOption | null;

        public creditCardErrors: PaymentCardValidity;

        constructor(
            private $scope: ng.IScope,
            private paymentsService: any,
            private paymentsUtilService: PaymentsUtilService,
            private broker: any
        ) {

            //use the viewPage property to determine which functions and watches we should be using.
            // This is only here so that we can support the old template for the cards
            this.usingOldTemplate = /v1/i.test(this.viewPage);

            const wait = $scope.$watch('pay.ready', b => {

                if(b) {
                    this.initPaymentOptions();
                    this.initSavedCards();

                    if (this.paymentAmount != null) {
                        $scope.$watch('pay.paymentAmount', (v:number) => {
                            if (v != null) {
                                this.setCardFee(v);
                            }
                        });
                    }

                    this.setDefaultSelection();

                    wait();
                }
            });


            if (this.usingOldTemplate) {
                $scope.$watch('pay.selectedSavedCard', (v: SavedCardOption) => {
                    if (v) {
                        this.savedCardSelected_TabVersion(v);
                    }
                });
            } else {
                //better to watch the change of options like this. Radio button onChange seems to get it self in to
                //a muddle and each click generates 3 changes!
                $scope.$watch('pay.selectedPaymentOptionId', (v: string) => {
                    if (v) {
                        if (v.length > 1) {
                            this.savedCardSelected(v);
                        } else {
                            this.paymentOptionChanged(parseInt(v,10));
                        }
                    }
                });
            }

            $scope.$watch('[pay.model.cardDetail.expiryDate.year]',
                (v: SavedCardOption) => {
                    const expiryDateYear = this.model.cardDetail.expiryDate.year;
                    const expiryDateMonth = this.model.cardDetail.expiryDate.month;
                if (expiryDateYear === this.currentYear) {
                    this.monthFrom = this.currentMonth + 1;
                    expiryDateMonth < this.currentMonth  ? this.model.cardDetail.expiryDate.month = 0 : null;
                    this.months = _.range(this.monthFrom, 13);
                } else {
                    this.months = _.range(1, 13);
                }
            });

            $scope.$watch('pay.creditCardErrors', (value:PaymentCardValidity, previousValue) => {
                if (value && value !== previousValue) {
                    if(value.invalid){
                        this.updateSelectedLogo(0);
                    }
                }
            });
        }

        updateCardType() {
            this.model.cardDetail.cardNumber = undefined;

            if (this.model.cardDetail.cardTypeId) {
                const card = _.find(this.paymentOptions, { 'CardTypeId' : this.model.cardDetail.cardTypeId});
                this.model.isPayPalPayment = false;
                this.model.cardDetail.isSavedCard = false;
                this.model.cardDetail.paymentOptionId = card.Id;
                this.model.cardDetail.paymentTypeId = card.PaymentType;
                this.model.cardDetail.cardTypeId = card.CardTypeId;
                this.fullCardDetailsRequired = card.FullDetailsRequired;
                this.isExternalFop = card.IsExternalFop;
                this.model.externalFop = this.paymentsUtilService.createEmptyExternalFop();
                this.setCardFee(this.paymentAmount);
                this.updateSelectedLogo(this.model.cardDetail.cardTypeId);
            } else {
                this.clearModel();
            }
        }

        selectTab(category: PaymentCategory): void {
            //USED in tab version of template
            this.clearModel();

            this.paymentCategory = category;

            if (category === PaymentCategory.SavedCard) {
                this.selectedSavedCard = this.savedCards[0];
            } else {
                this.selectedSavedCard = null;

                if (category === PaymentCategory.PayPal) {
                    this.selectPayPalOption();
                } else {
                    this.model.saveCard = TRPCONFIG.Domain.SavedCardsEnabled && this.cardCanBeSaved;
                }
            }
        }

        updateDfReferenceId(dfReferenceId) {
            this.onReceive({dfReferenceId});
        }

        addPreBookCommand(command) {
            this.addCommand({command});
        }

        checkCreditCardNumber(cardNumber) {
            this.cardTypeChanged = '';
            if (!cardNumber) { return; }

            this.paymentsService.checkCardNumber(this.paymentOptions,
                                                    cardNumber,
                                                    this.model.cardDetail.cardTypeId)
                .then(result => {
                        this.updateSelectedLogo(result);
                    }, result => {
                        const cardOption = _.find(this.paymentOptions, o => (o.CardTypeId === result));
                        let correctId = cardOption ? cardOption.CardTypeId : undefined;
                        if (this.model.cardDetail.cardTypeId !== correctId) {
                            this.clearModel();
                            this.model.cardDetail.cardTypeId = correctId;
                            this.updateCardType();
                            this.model.cardDetail.cardNumber = cardNumber;
                            this.cardTypeChanged = this.paymentsService.getCardTitle(this.paymentOptions, correctId);
                        }
                });
        }

        private selectPayPalOption(): void {
            this.model.isPayPalPayment = true;
            this.model.cardDetail.cardTypeId = this.payPalOption.CardTypeId;
            this.model.cardDetail.paymentOptionId = this.payPalOption.Id;
            this.model.cardDetail.paymentTypeId = this.payPalOption.PaymentType;
            this.setCardFee(this.paymentAmount);
        }

        private savedCardSelected(cardId: string): void {

            this.clearModel();

            const selectedCard = _.find(this.savedCards, { 'CardId' : cardId});

            this.fullCardDetailsRequired = selectedCard.FullDetailsRequired;
            this.model.isPayPalPayment = false;
            this.model.cardDetail.isSavedCard = true;
            this.model.cardDetail.paymentOptionId = selectedCard.CardType;
            this.model.cardDetail.paymentTypeId = selectedCard.PaymentTypeId;
            this.model.cardDetail.savedCardId = selectedCard.CardId;
            this.setCardFee(this.paymentAmount);

        }

        private savedCardSelected_TabVersion(card: SavedCardOption): void {
            //USED in tab version of template
            this.clearModel();

            this.model.isPayPalPayment = false;
            this.model.cardDetail.isSavedCard = true;
            this.model.cardDetail.savedCardId = card.CardId;
            this.model.cardDetail.paymentOptionId = card.CardType;
            this.model.cardDetail.paymentTypeId = card.PaymentTypeId;
            this.fullCardDetailsRequired = card.FullDetailsRequired;
            this.setCardFee(this.paymentAmount);
        }

        private paymentOptionChanged(category: PaymentCategory): void {

            this.clearModel();

            if (category=== PaymentCategory.Card) {
                this.model.saveCard = TRPCONFIG.Domain.SavedCardsEnabled && this.cardCanBeSaved;
            } else if (category === PaymentCategory.PayPal) {
                this.selectPayPalOption();
            }

        }

        private initPaymentOptions(): void {
            this.paymentOptions = angular.copy(this.piPaymentOptions);
            const paypalOptions = _.remove(this.paymentOptions,
                (o: PaymentOption) => { return o.IsPayPal === true; }
                );

            if (paypalOptions && paypalOptions.length > 0 && paypalOptions[0].Id === PaymentOptionId.PayPal){
                this.payPalOption = paypalOptions[0];
            }
            this.logoList = this.paymentsService.getLogosList(this.paymentOptions.concat(this.payPalOption|| []));
        }

        private updateSelectedLogo(selected):void {
            this.broker.publish('Qubit:TRB-4:cardTypeUpdated', { id: selected });
        }

        private initSavedCards(): void {

            if (this.piSavedCards && this.piSavedCards.length > 0) {
                this.savedCards = _.filter(this.piSavedCards, { 'IsExpired' : false});
                if (this.usingOldTemplate === false) {
                    this.savedCards = _.take(this.savedCards, 3);
                }
            } else {
                this.savedCards = [];
            }

            this.haveSavedCards = this.savedCards.length > 0;

            if (this.haveSavedCards && !this.savedCardsWatch) {
                //watch if our original list is ever changed
                this.savedCardsWatch = this.$scope.$watch('pay.piSavedCards', () => {
                    this.initSavedCards();
                    this.setDefaultSelection();
                });
            }

        }

        private setDefaultSelection(): void {
            if (this.savedCards && this.savedCards.length > 0) {

                if (this.usingOldTemplate) {
                    this.selectTab(PaymentCategory.SavedCard);
                } else {
                    this.selectedPaymentOptionId = this.savedCards[0].CardId; //default to the 1st card
                }

            } else {
                if (this.usingOldTemplate) {
                    this.selectTab(PaymentCategory.Card);
                } else {
                    this.selectedPaymentOptionId = PaymentCategory.Card.toString();
                    this.paymentOptionChanged(PaymentCategory.Card);
                }
            }
        }

        private setCardFee(total: number): void {

            //figure out which card type we are dealing with and update the
            //fee as required
            const setFee = (amount: number, total: number, percent: number): void => {
                const fee =  amount || total * (percent / 100);
                if (fee !== this.model.fee) {
                    this.model.fee = fee;
                }
                this.cardPercentage = percent;
            };

            if (this.model.cardDetail.isSavedCard === true) {
                //get saved card id, find card and update
                const card = _.find(this.savedCards, {'CardId': this.model.cardDetail.savedCardId});
                if(card) {
                    setFee(card.FeeAmount, total, card.FeePercent);
                }
            } else if (this.model.isPayPalPayment === true) {
                setFee(this.payPalOption.FeeAmount, total, this.payPalOption.FeePercent);
            } else if (this.model.cardDetail.cardTypeId != null) {
                //get payment option info and set fee
                const card = _.find(this.paymentOptions, {'CardTypeId' : this.model.cardDetail.cardTypeId});
                setFee(card.FeeAmount, total, card.FeePercent);
            }
        }

        private clearModel(): void {
            this.model = this.paymentsUtilService.createEmptyPaymentModel();
            this.fullCardDetailsRequired = false;
            this.isExternalFop = false;
            this.cardPercentage = 0;
        }
    }

    angular
        .module('core')
        .directive('payment', (): ng.IDirective => {
                return {
                    restrict: 'E',
                    replace: false,
                    transclude: true,
                    scope: {
                        piPaymentOptions: '=',
                        piSavedCards: '=',
                        atolProtected: '@',
                        cardCanBeSaved: '=',
                        flexiblePayments: '@',
                        modal: '=',
                        model: '=',
                        onReceive: '&',
                        addCommand: '&',
                        paymentAmount: '=',
                        ready: '=',
                        rootForm: '=',
                        viewPage: '@'
                    },
                    controller: Payment,
                    controllerAs: 'pay',
                    bindToController: true,
                    templateUrl: (element,attr) => {
                        if (attr.templateVersion) {
                            return `partials/booking/template?name=PaymentWithTabs&version=${attr.templateVersion}`;
                        }
                        return `partials/home/PaymentDirective?form=${attr.viewPage}`;
                    }
                };
            }
        );
}
